import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { usePartnerAuth } from '../context/PartnerAuthContext';
import { UserPlus, Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';

const PartnerRegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = usePartnerAuth();
  
  const selectedPlanFromState = location.state?.plan || 'amador';

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    plan_type: selectedPlanFromState,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const plans = [
    { id: 'amador', name: 'Parceiro Inicial', price: 'R$ 49,90/mes' },
    { id: 'profissional', name: 'Parceiro Profissional', price: 'R$ 89,90/mes' },
    { id: 'expert', name: 'Parceiro Avançado', price: 'R$ 129,90/mes' },
    { id: 'ocara', name: 'Parceiro Enterprise', price: 'R$ 249,90/mes' },
  ];

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm_password) {
      setError('As senhas nao coincidem');
      return;
    }
    if (form.password.length < 6) {
      setError('A senha deve ter no minimo 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const { confirm_password, ...registerData } = form;
      await register(registerData);
      navigate('/parceiro/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)' }}>
      <div className="w-full max-w-lg px-4">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors" data-testid="back-home-btn">
          <ArrowLeft className="w-4 h-4" /> Voltar ao site
        </button>

        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}>
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white" data-testid="register-title">Cadastro de Parceiro</h1>
            <p className="text-white/60 mt-2">Comece a vender na plataforma</p>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-200 px-4 py-3 rounded-xl mb-6 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-white/80 text-sm font-medium mb-1.5">Nome completo / Empresa</label>
              <input name="full_name" value={form.full_name} onChange={handleChange} required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Nome da sua empresa" data-testid="register-name-input" />
            </div>

            <div>
              <label className="block text-white/80 text-sm font-medium mb-1.5">Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="empresa@email.com" data-testid="register-email-input" />
            </div>

            <div>
              <label className="block text-white/80 text-sm font-medium mb-1.5">Telefone / WhatsApp</label>
              <input name="phone" value={form.phone} onChange={handleChange} required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="(11) 99999-9999" data-testid="register-phone-input" />
            </div>

            <div>
              <label className="block text-white/80 text-sm font-medium mb-1.5">Senha</label>
              <div className="relative">
                <input name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={handleChange} required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-500 pr-12"
                  placeholder="Minimo 6 caracteres" data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm font-medium mb-1.5">Confirmar senha</label>
              <input name="confirm_password" type="password" value={form.confirm_password} onChange={handleChange} required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Repita a senha" data-testid="register-confirm-password-input" />
            </div>

            {/* Plan Selection */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Plano</label>
              <div className="grid grid-cols-2 gap-2">
                {plans.map((plan) => (
                  <button key={plan.id} type="button"
                    onClick={() => setForm({ ...form, plan_type: plan.id })}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      form.plan_type === plan.id
                        ? 'border-green-500 bg-green-500/20 text-white'
                        : 'border-white/20 bg-white/5 text-white/60 hover:border-white/40'
                    }`}
                    data-testid={`plan-${plan.id}-btn`}
                  >
                    {form.plan_type === plan.id && <Check className="w-4 h-4 mx-auto mb-1 text-green-400" />}
                    <div className="text-sm font-medium">{plan.name}</div>
                    <div className="text-xs mt-0.5 opacity-70">{plan.price}</div>
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-xl font-bold text-white transition-all duration-300 disabled:opacity-50 mt-2"
              style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
              data-testid="register-submit-btn"
            >
              {loading ? 'Cadastrando...' : 'Criar Conta'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/50 text-sm">
              Ja tem conta?{' '}
              <Link to="/parceiro/login" className="text-green-400 hover:text-green-300 font-medium" data-testid="login-link">
                Entrar
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerRegisterPage;
